/**
 * Notification Endpoints
 */
import { getApiClient } from "../client";
import { NOTIFICATION_POPULATE } from "../populate";
import { EventQueryParamsSchema, EventSchema, MarkEventsReadInputSchema, NotificationQueryParamsSchema, NotificationSchema, RegisterNotificationTokenInputSchema, validateInput, } from "../schemas";
import { parseStrapiItemResponse, parseStrapiListResponse } from "../utils/parseStrapiResponse";
/**
 * Notification endpoints
 */
export const notificationsEndpoints = {
    /**
     * Get current user's notifications
     */
    async getMyNotifications(params) {
        const validated = params
            ? validateInput(NotificationQueryParamsSchema, params, "notificationsEndpoints.getMyNotifications")
            : undefined;
        const populate = NOTIFICATION_POPULATE.DEFAULT;
        const response = await getApiClient().get("/messaging/notifications/me", {
            params: {
                populate,
                sort: validated?.sort ?? ["sentDate:desc"],
                ...validated,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: NotificationSchema,
            populate,
            context: "notificationsEndpoints.getMyNotifications",
        });
    },
    /**
     * Register a notification token (for push notifications)
     */
    async registerToken(input) {
        const validated = validateInput(RegisterNotificationTokenInputSchema, input, "notificationsEndpoints.registerToken");
        const response = await getApiClient().post("/messaging/notification-tokens", { data: validated });
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Unregister a notification token
     */
    async unregisterToken(token) {
        const response = await getApiClient().delete(`/messaging/notification-tokens/${encodeURIComponent(token)}`);
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * Event endpoints (in-app notifications/events)
 * Note: Dashboard uses /bot-config-events paths
 */
export const eventsEndpoints = {
    /**
     * Get current user's events
     */
    async getMyEvents(params) {
        const validated = params ? validateInput(EventQueryParamsSchema, params, "eventsEndpoints.getMyEvents") : undefined;
        const response = await getApiClient().get("/bot-config-events/me", { params: validated });
        return parseStrapiListResponse(response.data, {
            schema: EventSchema,
            context: "eventsEndpoints.getMyEvents",
        });
    },
    /**
     * Mark events as read
     */
    async markEventsRead(input) {
        const validated = validateInput(MarkEventsReadInputSchema, input, "eventsEndpoints.markEventsRead");
        const response = await getApiClient().post("/bot-config-events/mark-read", {
            data: { eventIds: validated.eventIds },
        });
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Mark all events as read
     */
    async markAllEventsRead() {
        const response = await getApiClient().post("/bot-config-events/mark-all-read");
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * Messaging consents endpoints
 */
export const messagingConsentsEndpoints = {
    /**
     * Get current user's messaging consents
     */
    async getMyConsents() {
        const response = await getApiClient().get("/messaging/consents/me");
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Update current user's messaging consents
     */
    async updateMyConsents(consents) {
        const response = await getApiClient().put("/messaging/consents/me", consents);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Get user's messaging consents (admin)
     */
    async getUserConsents(userId) {
        const response = await getApiClient().get(`/messaging/consents/user/${userId}`);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Update user's messaging consents (admin)
     */
    async updateUserConsents(userId, consents) {
        const response = await getApiClient().put(`/messaging/consents/user/${userId}`, consents);
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Unsubscribe from a specific consent type (public, no auth required)
     */
    async unsubscribe(consentType, email) {
        const response = await getApiClient().put(`/messaging/consents/unsubscribe/${consentType}`, { email }, { headers: { Authorization: undefined } });
        return parseStrapiItemResponse(response.data);
    },
};
